import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useDoseTaskProcessing } from '~/backend/connector-api/connector-components';
import useProcessDicomFiles from '~/hooks/useProcessDicomFiles';
import useViewportEnvironment from '~/hooks/useViewportEnvironment';
import { useSnapshotInputApprovalStatus } from '~/hooks/workflows/useSnapshotInputApprovalStatus';
import { dosePredictionTaskState } from '~/state/atoms/dose/dosePredictionTaskAtom';
import { OptimizationObjective } from '~/domain/patient/optimizationObjective';
import { Job } from '~/state/atoms/jobAtoms';
import { selectedPatientIdState } from '~/state/atoms/patientAtoms';
import { workflowModalState, WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { handleError } from '~/utils/errors';
import {
    getHighestDoseQuantity,
    transformDoseTargets,
} from '~/utils/transforms/doseTargetsTransforms';
import { splitDicomAndJsonFiles } from '~/utils/io/files/FileHandler';
import {
    extractOptimizationObjectivesFile,
    readOptimizationObjectives,
} from '~/utils/transforms/optimizationObjectivesTransforms';
import { transformTaskToGenerationModel } from '~/utils/transforms/taskTransforms';
import { NewJobData, useJobManagement } from '../../useJobManagement';
import useEnsureImageCached from '../useEnsureImageCached';
import useStructureSetCacheUpdate from '../useStructureSetCacheUpdate';

type UseDosePredictionTaskProps = {
    patientId: string;
    imageStudyId: string;
    imageSeriesId: string;
    structureSetInstanceUid: string;
};

/**
 * A hook for creating a dose prediction task via Connector API.
 * @param {UseDosePredictionTaskProps} props - Contains study and series identifiers for the task.
 * @returns {Object} - An object containing the start function and submission state.
 */
export default function useDosePredictionTask({
    patientId,
    imageStudyId,
    imageSeriesId,
    structureSetInstanceUid,
}: UseDosePredictionTaskProps) {
    const selectedPatientId = useRecoilValue(selectedPatientIdState);
    const dosePredictionTaskOptions = useRecoilValue(dosePredictionTaskState);
    const predictObjectives = true;
    const setWorkflowModalState = useSetRecoilState(workflowModalState);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId?: string }>();

    const { mutateAsync: startDosePrediction } = useDoseTaskProcessing();
    const { ensureCached } = useEnsureImageCached();
    const updateCachedStructureSet = useStructureSetCacheUpdate();
    const { addJob, updateJob } = useJobManagement();
    const { processCreatedFiles } = useProcessDicomFiles();
    const { setEnvironmentDose } = useViewportEnvironment();
    const snapshotInputApprovalStatus = useSnapshotInputApprovalStatus();

    const handleProcessedFiles = async (job: Job) => {
        if (job.results && job.results?.length > 0) {
            // Split JSON files from DICOM files so processCreatedFiles only
            // receives genuine DICOM data. Any extra JSONs from the worker
            // (beyond optimization_objectives.json) would otherwise cause
            // DICOM parsing to fail and the dose to not be rendered.
            const { dicomFiles, jsonFiles } = splitDicomAndJsonFiles(
                job.results
            );
            const objFile = extractOptimizationObjectivesFile(jsonFiles);
            let objectives: OptimizationObjective[] = [];
            if (objFile) {
                try {
                    objectives = await readOptimizationObjectives(objFile);
                } catch (e) {
                    handleError(e, 'Failed to parse optimization objectives.');
                }
            }
            const generationModel = transformTaskToGenerationModel(job.task);
            const processedItems = await processCreatedFiles(
                dicomFiles,
                generationModel
            );
            if (processedItems) {
                if (objectives.length > 0) {
                    processedItems.doses.forEach((dose) => {
                        dose.optimizationObjectives = objectives;
                    });
                }
                updateJob(job.id, { isImported: true });
                const taskTargets =
                    job.task && 'targets' in job.task
                        ? job.task.targets
                        : undefined;
                if (taskTargets) {
                    const highestPrescription =
                        getHighestDoseQuantity(taskTargets);
                    for (const dose of processedItems.doses) {
                        dose.targets = taskTargets;
                        if (highestPrescription) {
                            dose.normalizationValueGy = highestPrescription;
                        }
                    }
                }
                setEnvironmentDose(
                    processedItems.doses,
                    selectedPatientId ?? ''
                );
            }
        }
    };
    /**
     * Creates the dose prediction task with the provided model.
     * @param {string} modelName - The model to use for the dose prediction task.
     */
    const start = async (modelName: string) => {
        if (isSubmitting) {
            return;
        }
        if (!dosePredictionTaskOptions?.targets) {
            handleError(new Error('No target defined.'));
            return;
        }

        setIsSubmitting(true);
        const inputApprovalStatus = await snapshotInputApprovalStatus(
            structureSetInstanceUid
        );
        try {
            // Make sure the connector actually has the input image
            // before asking it to process anything — the series may
            // have been generated locally (e.g. by Image+) and never
            // round-tripped through the connector cache.
            await ensureCached({
                patientId,
                seriesInstanceUID: imageSeriesId,
                studyInstanceUID: imageStudyId,
            });

            await updateCachedStructureSet();
            const task = await startDosePrediction({
                body: {
                    modelName,
                    imageSeries: {
                        patientID: patientId,
                        seriesInstanceUID: imageSeriesId,
                        studyInstanceUID: imageStudyId,
                    },
                    structureSet: {
                        sopInstanceUID: structureSetInstanceUid,
                        patientID: patientId,
                    },
                    doseTargets: transformDoseTargets(
                        dosePredictionTaskOptions.targets
                    ),
                    predictObjectives: predictObjectives,
                },
            });

            const newJob: NewJobData = {
                name: `Dose+`,
                id: task.id,
                type: WorkflowTypes.DOSE,
                task: {
                    targets: dosePredictionTaskOptions.targets,
                    model: dosePredictionTaskOptions.model,
                    output: task,
                },
                onLoad: handleProcessedFiles,
                inputApprovalStatus,
            };

            addJob(newJob);
            setWorkflowModalState((state) => ({
                ...state,
                open: false,
            }));

            const dosePath = sessionId
                ? `/session/${sessionId}/predictive-planning`
                : '/predictive-planning';
            navigate(dosePath);
        } catch (error) {
            handleError(error, 'Failed to start dose prediction task.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return { start, isSubmitting };
}
